import { type CSSProperties, ReactNode } from "react";

import { Collapse, type CollapseProps } from "antd";
import { clsx } from "clsx";

import styles from "./RVCollapse.module.scss";

export type PanelComponent = {
    /** Content shown in the panel header row. */
    header: ReactNode;
    /** Content rendered inside the expanded panel body. */
    content: ReactNode;
    /** Stable panel key; falls back to array index when omitted. */
    key?: string | number;
    /** Whether this panel is currently active/expanded (for controlled usage). */
    isActive?: boolean;
    /** Additional className applied to this specific panel. */
    className?: string;
    /** Inline style applied to this specific panel. */
    style?: CSSProperties;
    /** Controls visibility of the expand/collapse arrow icon. */
    showArrow?: boolean;
    /** Forces panel content to render even before first expansion. */
    forceRender?: boolean;
    /** @deprecated Use `collapsible="disabled"` instead */
    disabled?: boolean;
    /** Extra node rendered in the panel header (for actions, badges, etc.). */
    extra?: ReactNode;
};

export type RVCollapseProps = CollapseProps & {
    /** List of panels rendered within the Collapse */
    panelsList: PanelComponent[];
    /** Additional className for container */
    className?: string;
    /** Applies nested child styling */
    isChild?: boolean;
    /**
     * Visual variant of the collapse.
     * - `default`: standard bordered panels with a grey header background.
     * - `document`: transparent headers with no border and purple-4 header text (for right-rail document layouts).
     */
    variant?: "default" | "document";
};

/**
 * RVCollapse - Regal styled wrapper around Ant Design's Collapse component.
 * Provides consistent panel spacing and nested child styling across the app.
 */
export function RVCollapse({
    panelsList,
    className,
    expandIconPosition,
    expandIcon,
    isChild,
    variant = "default",
    ...rest
}: RVCollapseProps) {
    return (
        <Collapse
            expandIcon={expandIcon}
            expandIconPosition={expandIconPosition}
            className={clsx(
                styles.RVCollapseContainer,
                {
                    [styles.isChild]: isChild,
                    [styles.document]: variant === "document",
                },
                className
            )}
            {...rest}
        >
            {panelsList.map((panel, index) => {
                const { header, content, className: panelClassName, key, ...restPanelProps } = panel;

                return (
                    <Collapse.Panel
                        showArrow={panel.showArrow}
                        key={key ?? index}
                        header={header}
                        className={clsx(styles.RVCollapsePanel, panelClassName)}
                        {...restPanelProps}
                    >
                        {content}
                    </Collapse.Panel>
                );
            })}
        </Collapse>
    );
}
